import mixpanel, {Dict} from 'mixpanel-browser';

import {ENV} from '@/constants/EnvConstants';
import {IEventIdentifyDataDefinition} from '@/services/segment/models/IdentifyEventModel';
import {
  IEventDataSchema,
  TrackingEvent,
} from '@/services/segment/models/TrackingManagerEventModel';
import {ITrackingService} from '@/services/tracking/ITrackingService';
import {isLocalhost} from '@/utils/environment';

const invalidCharsRegex = /[^a-zA-Z0-9_-]/g;
const leadingUnderscoresRegex = /^_+/;

// Never initialized on localhost — `useTrackingManager` routes local dev
// through `mockTrackingService` instead, so this stays a safety net for
// any other place that might import this service directly.
let initialized = false;
const mixpanelToken = ENV.VITE_MIXPANEL_TOKEN;

if (!isLocalhost && mixpanelToken) {
  mixpanel.init(mixpanelToken, {
    persistence: 'cookie',
    cross_subdomain_cookie: true,
    secure_cookie: true,
    track_pageview: false,
    autocapture: false,
  });
  initialized = true;
}

export const mixpanelService = (): ITrackingService => {
  const trackNavigation = (path: string): void => {
    if (initialized) {
      const sanitizedPath =
        path
          .replace(invalidCharsRegex, '_')
          .replace(leadingUnderscoresRegex, '') || 'root';
      mixpanel.track(`navigate_to_${sanitizedPath}`);
    }
  };

  const trackEvent = (
    eventName: TrackingEvent,
    eventData: IEventDataSchema,
  ): void => {
    if (initialized) {
      mixpanel.track(eventName, eventData as Dict);
    }
  };

  const trackIdentifyEvents = (
    identity: string,
    eventData: IEventIdentifyDataDefinition,
  ) => {
    if (initialized) {
      mixpanel.identify(identity);
      mixpanel.people.set(eventData as Dict);
    }
  };

  const reset = () => {
    if (initialized) {
      mixpanel.reset();
    }
  };

  return {
    trackNavigation,
    trackEvent,
    trackIdentifyEvents,
    reset,
  };
};
